import './EditEvent.css'
import { ErrorAlert } from '../common/ErrorAlert';
import { useEffect, useState, useMemo } from "react";
import { Button, ConfigProvider, DatePicker, Form, Input, Select, Spin, TimePicker } from "antd";
import { Event, mapToEvent } from '../../models/calendar/Event.model';
import { useModalEvent } from '../../contexts/calendar/ModalEventContext.context';
import { useEvents } from '../../contexts/calendar/EventsContext.context';
import { EventService } from '../../services/events.api.client';
import { Dayjs } from 'dayjs';
import { useConfig } from '../../contexts/ConfigContext';
import { useCalendar } from '../../contexts/calendar/CalendarContext.context';
import { useCompany } from '../../contexts/CompanyContext';
import { useUser } from '../../contexts/UserContext';
import { Contact } from '../../services/contact.api.client';
import { ContactSearchSelect } from '../common/ContactSearchSelect';
import dayjs from '../../utils/dayjs'
import { EVENT_STATUS, EVENT_TYPE, statusOptions, typeOptions } from '../../constants/eventConstants';
import { CalendarCategory, CustomEventConfig } from '../../models/calendar/Calendar.model';
import { isOutsideCalendarHours } from '../../utils/calendarUtils';


type Props = {
    day:number,
    month:number,
    year:number
}

type FieldType = {
    summary: string;
    start: Dayjs | string;
    status: string;
    type: string;
    uidCalendar: string;
    uidCompany: string;
    end: Dayjs | string;
    hour: string;
    minute: string;
    duration: Dayjs | string;
    responsibleId: string;
    participantIds: string[];
};

const optionsStatus = statusOptions;
const typeOptionsData = typeOptions;

export type UserKeyValue = {
    label:string
    key:string
}

const CATEGORY_ALLOWED_TYPES: Record<string, string[]> = {
    GENERIC:    [],
    SCHEDULING: [EVENT_TYPE.EVENT.dbValue, EVENT_TYPE.CUSTOM_EVENT.dbValue, 'SCHEDULING', 'CUSTOM'],
    REMINDER:   [EVENT_TYPE.EVENT.dbValue, 'REMINDER'],
    TASK:       [EVENT_TYPE.TASK.dbValue, 'TASK'],
};

const format = 'HH:mm';

export const CreateEvent = ({day,month,year}:Props)=>{
    const now = new Date();
    const start = new Date(year,month,day,now.getHours(),now.getMinutes(),0)
    const end = new Date(year,month,day,now.getHours(),now.getMinutes(),0);
    end.setHours(end.getHours()+1);
    const {config} = useConfig();
    const startTime = dayjs(start);
    const endTime = dayjs(end);
    const [form] = Form.useForm<FieldType>();
    const [status] = useState<string>(EVENT_STATUS.PENDING.dbValue);
    const [showEndTime, setShowEndTime] = useState<boolean>(true);
    const [selectedCustomEvent, setSelectedCustomEvent] = useState<CustomEventConfig | undefined>();
    const [errorText,setErrorText] = useState<string>('');
    const [statusForm,setStatusForm] = useState<string>('SUCCESS');
    const {events, setEvents} = useEvents();
    const {options, setOptions} = useModalEvent();
    const {calendar} = useCalendar();
    const {company} = useCompany();
    const { user: currentUser } = useUser();

    // Generate dynamic type options from CompanyContext
    const dynamicTypeOptions = useMemo(() => {
        let opts = company.moduleOptions?.filter((opt: any) => opt.type === 'EVENT_TYPE' && opt.key !== 'GENERIC' && opt.show).map((opt: any) => ({
            label: opt.string_value,
            value: opt.key
        })) || [];
        
        // Fallback to constants if no dynamic options found
        if (opts.length === 0) {
            console.log("CreateEvent - Using fallback constants:", typeOptionsData);
            opts = typeOptionsData;
        }
        return opts;
    }, [company.moduleOptions]);

    // Filter event types by calendar category
    const calendarCategory: CalendarCategory = (calendar as any).category || 'GENERIC';
    const filteredTypeOptions = useMemo(() => {
        if (calendarCategory === 'GENERIC') return dynamicTypeOptions;
        const allowed = CATEGORY_ALLOWED_TYPES[calendarCategory];
        return dynamicTypeOptions.filter((opt: any) => allowed.includes(opt.value));
    }, [dynamicTypeOptions, calendarCategory]);

    const [contactMapping, setContactMapping] = useState<Record<string, Contact>>({});

    const handleContactsFound = (contacts: Contact[]) => {
        setContactMapping(prev => {
            const next = { ...prev };
            contacts.forEach(c => {
                next[c.uid] = c;
            });
            return next;
        });
    };
    
    useEffect(() => {
        if (options.isopen) {
            form.resetFields();
            const dNow = new Date();
            const startD = new Date(year, month, day, dNow.getHours(), dNow.getMinutes(), 0);
            const endD = new Date(year, month, day, dNow.getHours(), dNow.getMinutes(), 0);
            endD.setHours(endD.getHours() + 1);
            
            const defaultType = calendarCategory === 'GENERIC' 
                ? (dynamicTypeOptions.length > 0 ? dynamicTypeOptions.find((opt:any)=>opt.value===EVENT_TYPE.EVENT.dbValue)?.value || dynamicTypeOptions[0].value : EVENT_TYPE.EVENT.dbValue)
                : calendarCategory;

            form.setFieldsValue({
                start: dayjs(startD),
                end: dayjs(endD),
                type: defaultType
            });
            
            if (defaultType === EVENT_TYPE.TASK.dbValue) {
                setShowEndTime(false);
            } else {
                setShowEndTime(true);
            }
            
            setSelectedCustomEvent(undefined);
            setErrorText('');
        }
    }, [options.isopen, day, month, year, form]);

    const cerrarModal = ()=>{
        setOptions((prev)=>({...prev,isopen:false}))
    }

    const handleSubmit = (dataForm:FieldType) => {
        if(config.key == null || config.key == ''){
            setErrorText('No se ha podido obtener la clave de seguridad, intente nuevamente.');
            return;
        }
        
        const dateStart = (dataForm.start as Dayjs).second(0).millisecond(0);
        let dateEnd: Dayjs;
        if (showEndTime) {
            dateEnd = (dataForm.end as Dayjs).second(0).millisecond(0);
        } else {
            const durationMins = ((dataForm.duration as Dayjs).hour()) * 60 + ((dataForm.duration as Dayjs).minute());
            dateEnd = dateStart.add(durationMins, 'minute');
        }

        if (calendar.weekAvailable && isOutsideCalendarHours(dateStart, dateEnd, calendar.weekAvailable)) {
            setErrorText('El horario seleccionado se encuentra fuera del horario configurado para el calendario.');
            return;
        }

        // Validación de solapamiento por responsable (tolerancia de 5 minutos)
        if (showEndTime && dataForm.responsibleId) {
            const TOLERANCE_MS = 5 * 60 * 1000; // 5 minutos
            const newStart = dateStart.valueOf();
            const newEnd = dateEnd.valueOf();

            const conflict = events.find((ev) => {
                if (ev.responsibleId !== dataForm.responsibleId) return false;
                if (!ev.start || !ev.end) return false;
                const evStart = new Date(ev.start).getTime();
                const evEnd = new Date(ev.end).getTime();
                // Hay conflicto si el solapamiento real es mayor a 5 minutos
                const overlapStart = Math.max(newStart, evStart);
                const overlapEnd = Math.min(newEnd, evEnd);
                const overlapMs = overlapEnd - overlapStart;
                return overlapMs > TOLERANCE_MS;
            });

            if (conflict) {
                setErrorText(`El responsable ya tiene un evento programado ("${conflict.summary}") en ese horario. Se permite un solapamiento máximo de 5 minutos.`);
                return;
            }
        }

        const data:Record<string, any> = {
            uidCalendar: dataForm.uidCalendar,
            calendar_id: calendar.id, // Numeric ID from context
            uidCompany: dataForm.uidCompany,
            company_id: company.current.id, // Numeric ID
            summary: dataForm.summary,
            start: dateStart.format("YYYY-MM-DDTHH:mm:ssZ"),
            end: showEndTime ? dateEnd.format("YYYY-MM-DDTHH:mm:ssZ"): '',
            status: dataForm.status,
            type: dataForm.type,
            duration: showEndTime ? 0 : (((dataForm.duration as Dayjs).hour())*60 + (dataForm.duration as Dayjs).minute()),
            createdBy: currentUser.uid,
            created_by_id: currentUser.id, // Numeric ID
            creator_enriched: {
                id: currentUser.id,
                uid: currentUser.uid,
                name: currentUser.name
            },
            responsible: contactMapping[dataForm.responsibleId] ? { 
                id: contactMapping[dataForm.responsibleId].id, 
                uid: contactMapping[dataForm.responsibleId].uid, 
                name: contactMapping[dataForm.responsibleId].name 
            } : null,
            participants: dataForm.participantIds?.map(uid => contactMapping[uid]).filter(Boolean).map(p => ({
                id: p.id,
                uid: p.uid,
                name: p.name
            })),
            responsibleId: dataForm.responsibleId,
            participantIds: dataForm.participantIds
        };
        setStatusForm('LOAD');
        // We pass the full enriched data object to the service
        EventService.createEvent(data, dataForm.uidCompany, dataForm.uidCalendar, config.key).then((response)=>{
                const mappedEvent = mapToEvent(response) as Event;
                setEvents((prevEvents:Event[])=>{
                    const events = [...prevEvents];
                    events.push(mappedEvent);
                    return events;
                });
                setStatusForm('SUCCESS');
                setOptions((prev)=>({...prev,isopen:false}));
                form.resetFields();
                setSelectedCustomEvent(undefined);
                setErrorText('');
        }).catch((error_)=>{
            setErrorText(error_.message);
            setStatusForm('SUCCESS');
        })
    };

    switch(statusForm){
        case 'LOAD':
            return(
                <div className="w-[100%] container-load">
                    <Spin size="large" />
                </div>
            );
        default:
            return (
                <ConfigProvider
                    theme={{
                        components: {
                        Form: {
                            itemMarginBottom: 8
                        },
                        },
                    }}
                >
                    <div style={{paddingLeft:20,paddingRight:20}}>
                        <ErrorAlert message={errorText} onDismiss={() => setErrorText('')} />
                    </div>
                    <Form className="create-form" style={{paddingLeft:20,paddingRight:20,paddingBottom:8}} onFinish={handleSubmit} 
                        form={form} layout="vertical"
                        initialValues={{
                            uidCalendar: calendar.uid,
                            uidCompany: company.current.uid,

                            start: startTime,
                            end: endTime,
                            status: status,
                            type: calendarCategory === 'GENERIC' 
                                  ? (filteredTypeOptions.length > 0 ? filteredTypeOptions.find((opt:any)=>opt.value===EVENT_TYPE.EVENT.dbValue)?.value || filteredTypeOptions[0].value : EVENT_TYPE.EVENT.dbValue)
                                  : calendarCategory,
                        }}>
                        <Form.Item label={null} hidden={true} name="uidCalendar">
                            <input type="hidden" name="uidCalendar"/>
                        </Form.Item>
                        <Form.Item label={null} hidden={true} name="uidCompany">
                            <input type="hidden" name="uidCompany"/>
                        </Form.Item>

                        {calendar?.customEvents && calendar.customEvents.length > 0 && (
                            <Form.Item label="Plantilla de Evento (Opcional)" style={{marginBottom:8}}>
                                <Select 
                                    allowClear
                                    placeholder="Seleccionar evento personalizado..."
                                    options={calendar.customEvents.map((evt: CustomEventConfig) => ({
                                        label: `${evt.name} (${evt.duration} min)`,
                                        value: evt.id!
                                    }))}
                                    onChange={(value) => {
                                        if (value) {
                                            const evt = calendar.customEvents?.find((e: CustomEventConfig) => e.id === value);
                                            setSelectedCustomEvent(evt);
                                            const currentStart = form.getFieldValue('start') as Dayjs | undefined;
                                            if (currentStart && evt) {
                                                form.setFieldValue('end', dayjs(currentStart.toISOString()).add(evt.duration, 'minute'));
                                                if (!form.getFieldValue('summary')) {
                                                    form.setFieldValue('summary', evt.name);
                                                }
                                            }
                                            if (calendarCategory === 'GENERIC') {
                                                const customType = filteredTypeOptions.find((opt:any)=>opt.value==='CUSTOM')?.value || EVENT_TYPE.CUSTOM_EVENT.dbValue;
                                                form.setFieldValue('type', customType);
                                                setShowEndTime(true);
                                            }
                                        } else {
                                            setSelectedCustomEvent(undefined);
                                            if (calendarCategory === 'GENERIC') {
                                                const customType = filteredTypeOptions.find((opt:any)=>opt.value==='CUSTOM')?.value || EVENT_TYPE.CUSTOM_EVENT.dbValue;
                                                if (form.getFieldValue('type') === customType || form.getFieldValue('type') === EVENT_TYPE.CUSTOM_EVENT.dbValue) {
                                                    const fallbackType = filteredTypeOptions.length > 0 ? filteredTypeOptions.find((opt:any)=>opt.value===EVENT_TYPE.EVENT.dbValue)?.value || filteredTypeOptions[0].value : EVENT_TYPE.EVENT.dbValue;
                                                    form.setFieldValue('type', fallbackType);
                                                    setShowEndTime(true);
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Form.Item>
                        )}

                        <Form.Item<FieldType>
                            label="Titulo"
                            name="summary"
                            rules={[{ required: true, message: 'Ingrese un titulo para el evento!' }]}
                            style={{width:'100%',marginBottom:8}}
                        >
                            <Input type="text" name="summary"/>
                        </Form.Item>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,width:'100%'}}>
                            <Form.Item<FieldType>
                                label="Inicio"
                                name="start"
                                rules={[{ required: true, message: 'Ingrese un fecha de inicio para el evento!' }]}
                                style={{width:'100%',marginBottom:0}}
                            >
                                <DatePicker
                                    style={{width:'100%'}}
                                    showTime={{ format: 'HH:mm', minuteStep: 10 }}
                                    format="YYYY-MM-DD HH:mm"
                                    needConfirm={false}
                                    onChange={(date) => {
                                        if (date && selectedCustomEvent) {
                                            form.setFieldValue('end', dayjs(date).add(selectedCustomEvent.duration, 'minute'));
                                        }
                                    }}
                                />
                            </Form.Item>                    


                            {showEndTime&&<Form.Item<FieldType>
                                label="Fin"
                                name="end"
                                rules={[{ required: true, message: 'Ingrese un fecha de fin para el evento!' }]}
                                style={{width:'100%',marginBottom:0}}
                            >
                                <DatePicker
                                    style={{width:'100%'}}
                                    showTime={{ format: 'HH:mm', minuteStep: 10 }}
                                    format="YYYY-MM-DD HH:mm"
                                    needConfirm={false}
                                />
                            </Form.Item>}
                            {!showEndTime&&<Form.Item<FieldType>
                                label="Duración"
                                name="duration"
                                rules={[{ required: true, message: 'Ingrese una duración para la tarea!' }]}
                                style={{width:'100%',marginBottom:8,display:'flex',alignItems:'center'}}
                            >
                                <TimePicker format={format} showNow={false} minuteStep={10} />
                            </Form.Item>}
                        
                            <Form.Item<FieldType>
                                label="Estado"
                                name="status"
                                rules={[{ required: true, message: 'Ingrese un estado para el evento!' }]}
                                className="space-y-2"
                            >
                                <Select
                                    className="max-w-xs"
                                    placeholder="Seleccione un estado"
                                >
                                    {
                                        optionsStatus.map((option) => (
                                            <Select.Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Select.Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>   
                            <Form.Item<FieldType>
                                label="Tipo de Evento"
                                name="type"
                                rules={[{ required: true, message: 'Ingrese un tipo de evento!' }]}
                                className="space-y-2"
                            >
                                <Select
                                    className="max-w-xs"
                                    disabled={calendarCategory !== 'GENERIC'}
                                    onChange={(option:string) => {
                                            if (option === EVENT_TYPE.TASK.dbValue) {
                                                setShowEndTime(false);
                                            } else {
                                                setShowEndTime(true);
                                            }
                                        }
                                    }
                                    placeholder="Seleccione un tipo"
                                    >
                                        {
                                            filteredTypeOptions.map((option:any) => (
                                                <Select.Option key={option.value} value={option.value}>
                                                    {option.label}
                                                </Select.Option>
                                            ))
                                        }
                                </Select>
                            </Form.Item>   
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,width:'100%'}}>
                            <Form.Item<FieldType>
                                label="Responsable (Empleado)"
                                name="responsibleId"
                                rules={[{ required: true, message: 'Seleccione un responsable!' }]}
                            >
                                <ContactSearchSelect 
                                    role="EMPLOYEE"
                                    placeholder="Seleccione un Responsable"
                                    onSelectContact={(c) => handleContactsFound([c])}
                                />
                            </Form.Item>
                            <Form.Item<FieldType>
                                label="Participantes (Clientes)"
                                name="participantIds"
                            >
                                <ContactSearchSelect 
                                    mode="multiple"
                                    role="CUSTOMER"
                                    placeholder="Seleccione Participantes"
                                    onSelectContacts={handleContactsFound}
                                />
                            </Form.Item>
                        </div>

                        <div style={{paddingTop:8,display:'flex',justifyContent:'flex-end',alignItems:'center',gap:8,width:'100%'}}>
                            <Form.Item label={null} style={{marginBottom:0}}>
                                <Button type="primary" htmlType="submit">
                                    Crear
                                </Button>
                            </Form.Item>
                            <Form.Item label={null} style={{marginBottom:0}}>
                                <Button onClick={()=>cerrarModal()}>
                                    Cerrar
                                </Button>
                            </Form.Item>                        
                        </div>
                    </Form>
                </ConfigProvider>
            )
    }
}
